import { useQuery } from "@tanstack/react-query";
import { client as apiClient } from "../../services/api/client.gen";
import type { ApiEnvelope } from "../../types/loras";

interface AssetUrlData {
  downloadUrl?: string;
  thumbnailUrl?: string | null;
}

/**
 * Resolve a single asset id to a displayable (signed) URL via
 * `GET /v1/assets/:assetId`. Used to render LoRA checkpoint sample-preview
 * thumbnails from `checkpoint.sampleInferenceAssetIds` (Phase 4).
 *
 * Prefers `thumbnailUrl` when present, else the full `downloadUrl`. The
 * backend returns short-lived signed URLs (~1h), so we keep them fresh-ish
 * with a 5-minute staleTime rather than caching indefinitely.
 */
export function useAssetUrl(assetId: string | null | undefined) {
  return useQuery({
    queryKey: ["asset-url", assetId] as const,
    enabled: Boolean(assetId),
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const res = await apiClient.get<ApiEnvelope<AssetUrlData>>({
        url: `/v1/assets/${assetId}`,
      });
      const data = res.data?.data;
      return data?.thumbnailUrl || data?.downloadUrl || null;
    },
  });
}
